import { Messages } from "../../../model/Message";
import { InfoBoard } from "../../../components/infoBoard/InfoBoard";
import { convertDateTimeToString, convertDateToString, convertTimeToString, convertDateToJapaneseZeroPadString, convertDateTimeToJapaneseString } from "../../../utility/ConvertUtily";
import Badge from "../../../components/badges/Badges";
import { TopicsMap } from "../../../model/TopicsMap";
import { ReplyButton } from "../../../model/ReplyButton";
import { OnlineUrls } from "../../../model/OnlineUrls";
import { useEffect, useRef, useState } from "react";

export type MessageProps = {
  value: Messages;
  topicsMap: TopicsMap[];
  replyButton?: ReplyButton;
  onlineUrlList?: OnlineUrls[];
};

export const MessageRef = ({ value, topicsMap, replyButton, onlineUrlList
}: MessageProps) => {

  // 初期サイズ
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{height: number}>({height: 450});
  const [resizing, setResizing] = useState(false);

  const onelineUrl = onlineUrlList?.filter((item) => item.url === value?.link)[0];

  useEffect(() => {
    const handle = containerRef.current?.querySelector(".resize-handle");

    if (handle) {
      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        setResizing(true);
      };

      // 型をちゃんと Element にして、イベントも合うように cast
      handle.addEventListener("mousedown", onMouseDown as EventListener);

      const handleMouseMove = (e: MouseEvent) => {
        if (resizing && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const newHeight = e.clientY - rect.top;
          setSize({
            height: Math.max(newHeight, 200),
          });
        }
      };

      const stopResize = () => {
        setResizing(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResize);

      return () => {
        handle.removeEventListener("mousedown", onMouseDown as EventListener);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", stopResize);
      };
    }
  }, [resizing]);

  // 配信状態の切り替え
  let publishState = (<Badge color="green">配信済み</Badge>);
  if(value.deliveredFlg) {
    publishState = (<Badge color="green">配信済み</Badge>);
  } else if(value.publishEndAt !== undefined && value.publishEndAt <= new Date()) {
    publishState = (<Badge color="gray">掲載終了</Badge>);
  } else {
    publishState = (<Badge color="red">未配信</Badge>);
  };
  if(value.draftFlg) {
    publishState = (<Badge color="blue">下書き</Badge>);
  };

  // 掲載終了日
  const publishEndDate = Number.isNaN(value?.publishEndAt?.getTime())
    ? undefined
    : value?.publishEndAt;

  let file: { [key: string]: any } = {
    '　　配信状態': publishState,
    '　　　配信先': (
      <div className="flex flex-wrap">
        {value?.topic?.map((groupId, index) => {
          let groupName: string;
          if(groupId === "all"){
            groupName = "全体";
          } else {
            groupName = topicsMap.filter((group) => group.id === groupId)[0].name ?? groupId;
          }
          return (
          <div key={`${groupId}-${index}`} className="mr-1 mb-1">
            <Badge color="yellow">{groupName}</Badge>
          </div>
        )})}
      </div>
    ),
    '　　　　本文': value?.body,
  };
  if(value?.image) {
    file = {...file,
      '　　　　画像': (<img src={value?.image} alt={'添付画像'} className={"max-h-[16rem] max-w-[16rem]"}/>),
    };
  }
  if(value){
    file = {
      ...file,
      '　URLリンク': value?.urlButtonFlg ? `${value?.urlButtonName}: ${value?.urlLink}` : 'なし',
      '問い合わせ先': value?.contactInformation,
      '　返信ボタン': replyButton?.id !== "undefined" && replyButton !== undefined
      ? `${replyButton?.name}: ${replyButton?.buttonName.join(" / ")}`
      : "なし",
      '　掲載終了日': publishEndDate !== undefined ? convertDateToJapaneseZeroPadString(new Date(publishEndDate)) : '',
      '　　作成日時': `${convertDateToJapaneseZeroPadString(new Date(value?.createAt))} ${convertTimeToString(new Date(value?.createAt))}`,
    };
  };
  if(value?.eventFlg) {
    file = {...file,
      '会場': value?.eventVenue,
      'イベントタイプ': value?.eventType,
      'ミーティングURL': onelineUrl?.id !== "none" && onelineUrl?.url !== ""
        ? `${onelineUrl !== undefined ? onelineUrl?.name : "編集済みのURL"}: ${value?.link}`
        : "設定なし",
      'イベント開催日': convertDateToString(value?.eventAt),
      '開始時間': convertTimeToString(value?.eventAt),
      '終了時間': convertTimeToString(value?.eventEndAt),
    };
  }

  const title = value?.title === "" || value?.title === null
    ? (<span className="text-gray-300">タイトルなし</span>)
    : value?.title

  return (
    <>
      <div>
        <div className="max-w-full px-5">
          <h2 className="text-sm text-gray-900 mb-2">{`配信日時：${convertDateToJapaneseZeroPadString(new Date(value?.sendDate))} ${convertTimeToString(new Date(value?.sendDate))}`}</h2>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>

          <div
            ref={containerRef}
            className="relative border shadow overflow-hidden resize-y"
            style={{
              width: "100%",
              minWidth: "450px",
              height: size.height,
            }}
          >
            <div className="h-full w-full overflow-auto p-4 bg-sky-50">
              <InfoBoard col={"col1"} filed={file} children={undefined} flexDisp />
            </div>

            <div
              className="absolute bottom-0 right-0 w-5 h-5 cursor-ns-resize"
            >
              <div className="w-full h-full bg-gray-300 opacity-60" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
